import { Component, Inject, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import {AbstractControl, FormBuilder, NgControl, ValidationErrors, Validators} from '@angular/forms';
import { Subscription, Subject } from 'rxjs';
import { APP_CONFIG } from '../../../app.config';
import { AssistantPopoverService } from '../../../common/assistant-popover.service';
import { ValidationService } from '../../../common/validation.service';
import { ValueListsService } from '../../../common/value-lists.service';
import { AppConfig } from '../../../models/config/app-config';
import { FormSubGroupControl } from '../../../models/formControls/FormSubGroupControl';
import { VisumErfassungSubFormComponent } from '../visum-erfassung-sub-form/visum-erfassung-sub-form.component';
import { PassRestrictedCheckboxTextKey } from '../../../shared/components/pass-restricted-checkbox/pass-restricted-checkbox-texts/pass-restricted-checkbox-texts.component';
import { DateUtilsService } from '../../../common/date-utility.service';
import { BiometrieFingerprintStateService } from '../../../common/biometrie-fingerprint-state.service';
import { startWith } from 'rxjs/operators';

@Component({
  selector: 'app-biometrie-kurzfristiger-aufenthalt',
  templateUrl: './biometrie-kurzfristiger-aufenthalt.component.html',
  styleUrls: ['./biometrie-kurzfristiger-aufenthalt.component.scss']
})
export class BiometrieKurzfristigerAufenthaltComponent extends VisumErfassungSubFormComponent implements OnInit, OnDestroy {
  private fingerabdrueckeBereitsErfasstSub: Subscription;

  readonly fingerabdrueckeBereitsErfasstSelected$ = new Subject<string | null>();
  fingerabdrueckeErfassungsDatumUiErrors: ValidationErrors | null = null;

  private readonly MAX_MONTHS = 59;
  private fingerabdrueckeErfassungsDatumStatusSub?: Subscription;


  readonly fingerabdrueckeTitleKey: PassRestrictedCheckboxTextKey = 'BIOMETRIE_FINGERABDRUECKE_TITLE';
  readonly fingerabdrueckeLabels: Record<string, PassRestrictedCheckboxTextKey> = {
    YES: 'BIOMETRIE_FINGERABDRUECKE_YES',
    NO: 'BIOMETRIE_FINGERABDRUECKE_NO'
  };

  constructor(
    fb: FormBuilder,
    private valueListsService: ValueListsService,
    private validationService: ValidationService,
    private dateUtils: DateUtilsService,
    private biometrieFpState: BiometrieFingerprintStateService,
    assistantPopoverService: AssistantPopoverService,
    @Optional() @Self() controlDir: NgControl,
    @Inject(APP_CONFIG) public appConfig: AppConfig
  ) {
    super(controlDir, assistantPopoverService);

    this.form = this.wrapFormGroup(
      fb.group({
        fingerabdrueckeBereitsErfasst: [null, []],
        fingerabdrueckeBereitsErfasstAuswahl: [
          null,
          [ Validators.required ]
        ],
        fingerabdrueckeErfassungsDatum: [
          null,
          validationService.getValidators('antragsteller.biometrie.fingerabdrueckeErfassungsDatum')
        ],
        stickernummer: new FormSubGroupControl()
      })
    );
  }

  ngOnInit() {
    super.ngOnInit();
    this.fingerabdrueckeBereitsErfasstSub =
      this.form.controls.fingerabdrueckeBereitsErfasstAuswahl.valueChanges.subscribe((val: string | null) => {
        const boolVal = val === 'YES' ? true : val === 'NO' ? false : null;
        this.form.controls.fingerabdrueckeBereitsErfasst.setValue(boolVal, { emitEvent: false });
        this.enableDisableControls(boolVal === true);
      });
    this.enableDisableControls(this.form.value.fingerabdrueckeBereitsErfasst === true);

    const dateCtrl = this.form.controls.fingerabdrueckeErfassungsDatum;

    this.fingerabdrueckeErfassungsDatumStatusSub = dateCtrl.valueChanges
      .pipe(startWith(dateCtrl.value))
      .subscribe(() => this.updateDateUiState());
  }

  ngOnDestroy() {
    this.fingerabdrueckeBereitsErfasstSub?.unsubscribe();
    this.fingerabdrueckeErfassungsDatumStatusSub?.unsubscribe();
    this.biometrieFpState.setBiometrieDateOlderThanMaxMonths(false);
  }

  onValidatorUpdate() {
    super.onValidatorUpdate();
    const boolVal = this.form.controls.fingerabdrueckeBereitsErfasst.value as (boolean | null);
    const key = boolVal === true ? 'YES' : boolVal === false ? 'NO' : null;
    this.form.controls.fingerabdrueckeBereitsErfasstAuswahl.setValue(key, { emitEvent: false });
    this.fingerabdrueckeBereitsErfasstSelected$.next(key);
    this.enableDisableControls(boolVal === true);
  }

  protected isVidex(): boolean {
    return this.appConfig?.umgebung === 'VIDEX';
  }

  private updateDateUiState(): void {
    const dateCtrl = this.form.controls.fingerabdrueckeErfassungsDatum;

    if (this.form.value.fingerabdrueckeBereitsErfasst !== true) {
      this.fingerabdrueckeErfassungsDatumUiErrors = null;
      this.biometrieFpState.setBiometrieDateOlderThanMaxMonths(false);
      return;
    }

    const raw = dateCtrl.value;

    const ctrlErrors = dateCtrl.errors ?? null;

    let tooOldErr: ValidationErrors | null = null;
    if (!this.isVidex() && raw != null && raw !== '') {
      const stub = { value: raw } as AbstractControl;
      tooOldErr = this.dateUtils.maxMonthsAgoValidator(this.MAX_MONTHS)(stub);
    }

    const merged: ValidationErrors = {
      ...(ctrlErrors ?? {}),
      ...(tooOldErr ?? {})
    };

    this.fingerabdrueckeErfassungsDatumUiErrors =
      Object.keys(merged).length ? merged : null;

    this.biometrieFpState.setBiometrieDateOlderThanMaxMonths(
      !this.isVidex() && !!tooOldErr?.olderThanMaxMonths
    );
  }

  private enableDisableControls(fingerabdrueckeBereitsErfasst: boolean) {
    const dateCtrl = this.form.controls.fingerabdrueckeErfassungsDatum;

    if (fingerabdrueckeBereitsErfasst) {
      const baseValidators =
        this.validationService.getValidators('antragsteller.biometrie.fingerabdrueckeErfassungsDatum') ?? [];

      dateCtrl.enable({ emitEvent: false });
      dateCtrl.setValidators([Validators.required, ...baseValidators, this.dateUtils.noZeroMonthValidator()]);

      dateCtrl.updateValueAndValidity({ emitEvent: false });

      this.updateDateUiState();
    } else {
      dateCtrl.clearValidators();
      dateCtrl.setErrors(null);
      dateCtrl.disable({ emitEvent: false });
      dateCtrl.setValue(null, { emitEvent: false });
      dateCtrl.updateValueAndValidity({ emitEvent: false });

      this.fingerabdrueckeErfassungsDatumUiErrors = null;
      this.biometrieFpState.setBiometrieDateOlderThanMaxMonths(false);
    }
  }
  protected readonly APP_CONFIG = APP_CONFIG;
}
